import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { withAllContexts } from '../../AllContextsWrapper';
import { useSnackbar } from 'notistack';
import { ArrowLeft, Settings, Power } from 'lucide-react';

const AdminPluginsListPage = (props) => {
    const { id: calendarId } = useParams();
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();

    const [isLoading, setIsLoading] = useState(true);
    const [plugins, setPlugins] = useState([]);
    const [calendarName, setCalendarName] = useState('');

    useEffect(() => {
        if (props.socketIO && props.socketIO.socket) {
            fetchPlugins();
        }
    }, [calendarId, props.socketIO]);

    const fetchPlugins = () => {
        setIsLoading(true);
        props.socketIO.socket.emit('calendars:getPlugins', parseInt(calendarId), (response) => {
            if (response.success) {
                setPlugins(response.data);
                // Also fetch calendar name if possible, or just ignore
            } else {
                enqueueSnackbar("Nepodarilo sa načítať pluginy", { variant: 'error' });
            }
            setIsLoading(false);
        });

        // Fetch basic calendar info for header
        props.socketIO.socket.emit('calendars:getAllAdmin', (response) => {
            if (response.success) {
                const cal = response.data.find(c => c.id === parseInt(calendarId));
                if (cal) setCalendarName(cal.name);
            }
        });
    };

    const handleTogglePlugin = (plugin) => {
        const newEnabled = !plugin.enabled;
        // Optimistic update
        const newPlugins = plugins.map(p => p.plugin_name === plugin.plugin_name ? { ...p, enabled: newEnabled } : p);
        setPlugins(newPlugins);

        props.socketIO.socket.emit('calendars:updatePlugin', {
            calendarId: parseInt(calendarId),
            pluginName: plugin.plugin_name,
            enabled: newEnabled,
            settings: plugin.settings
        }, (response) => {
            if (response.success) {
                enqueueSnackbar(`Plugin ${newEnabled ? 'zapnutý' : 'vypnutý'}`, { variant: 'success' });
            } else {
                enqueueSnackbar("Chyba pri aktualizácii", { variant: 'error' });
                fetchPlugins(); // Revert
            }
        });
    };

    const handleOpenSettings = (pluginName) => {
        history.push(`/admin/calendar/${calendarId}/plugins/${pluginName}`);
    };

    if (isLoading) return <div className="p-8 text-center text-gray-500">Načítavam...</div>;

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* Header */}
            <div className="bg-white border-b px-4 md:px-6 py-4 flex flex-col md:flex-row md:items-center gap-4 sticky top-0 z-10 shadow-sm">
                <div className="flex items-center gap-4 w-full">
                    <button
                        onClick={() => history.push('/')}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-600 flex-shrink-0"
                    >
                        <ArrowLeft size={24} />
                    </button>
                    <div className="min-w-0">
                        <div className="text-sm text-gray-500">Administrácia / Pluginy</div>
                        <div className="text-xl font-bold text-gray-900 truncate">
                            {calendarName ? `Pluginy - ${calendarName}` : 'Pluginy'}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto mt-4 md:mt-8 px-4 md:px-6">
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                    {plugins.length === 0 ? (
                        <div className="p-8 text-center text-gray-500">Žiadne dostupné pluginy.</div>
                    ) : (
                        <div className="divide-y divide-gray-100">
                            {plugins.map(plugin => (
                                <div key={plugin.plugin_name} className="p-4 md:p-6 flex flex-col md:flex-row md:items-center justify-between hover:bg-gray-50 transition-colors gap-4">
                                    <div className="flex items-center gap-4">
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${plugin.enabled ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-400'}`}>
                                            {/* Icon placeholder - maybe map plugin names to icons later */}
                                            <div className="font-bold text-lg">{plugin.plugin_name.substring(0, 2).toUpperCase()}</div>
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-semibold text-gray-900 capitalize">{plugin.plugin_name}</h3>
                                            <p className="text-sm text-gray-500">
                                                {plugin.enabled ? 'Aktívny' : 'Neaktívny'}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full md:w-auto pl-[4rem] md:pl-0">
                                        <button
                                            onClick={() => handleTogglePlugin(plugin)}
                                            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${plugin.enabled
                                                ? 'bg-green-100 text-green-700 hover:bg-green-200'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                        >
                                            <Power size={18} />
                                            {plugin.enabled ? 'Zapnuté' : 'Vypnuté'}
                                        </button>

                                        {plugin.enabled && (
                                            <button
                                                onClick={() => handleOpenSettings(plugin.plugin_name)}
                                                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2 shadow-sm"
                                            >
                                                <Settings size={18} />
                                                Nastavenia
                                            </button>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default withAllContexts(AdminPluginsListPage);
